import { useMemo } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map, omit, concat } from "lodash"
import { CardContent, Paper } from "@mui/material"
import { useThemeChartColors, applyRiskChartColorsToItems } from "hooks/use-theme-chart-colors"
import { PERIOD, DATE } from "helpers/shapes"
import { smallPercentageFormatter, percentageFormatter, oneDecimalPercentageFormatter } from "helpers/formatters/numbers"
import { computeCSVData } from "helpers/csv"
import { useTrack } from "helpers/analytics"
import RiskDetails from "modules/analyze-composite-risk-details"
import MultiLine from "components/charts/multi-line"
import MultiLineDoubleYAxis from "components/charts/multi-line-double-y-axis"
import MultiLineBreakdown from "components/charts/multi-line-breakdown"
import Tooltip from "components/analyze-tooltip"
import BreakdownTooltip from "components/breakdown-tooltip"
import NoData from "components/no-data"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import "./style.scss"

export default function AnalyzeCompositeRisk({
  account,
  modelId,
  analyzeViewSelectList,
  positionSetId,
  positionSetType,
  positionSetName,
  base,
  baseSignature,
  classifications,
  allCategoryItems,
  risk,
  currentGrouping,
  setGrouping,
  expand,
  showNotHeld,
  factorCategoryId,
  selectFactorCategory,
  earliestDate,
  latestDate,
  setDate,
  selectedDate,
  startDate,
  endDate,
  fetchStartDate,
  data,
  items,
  options,
  toggle,
  focusChartLine,
  blurChartLine,
  focusedChartLine,
  viewType,
  fileName,
  drift,
  selectedOption,
  toggleOnly,
  only,
  filtersVisible,
  selectedViews,
  openDialog,
  displayAddView,
  availableSelectViewFactors,
  yScaleIds,
  segment,
  showSegment,
  addSelectedView,
  removeSelectedView,
}) {
  const trackExcelDownload = useTrack({
    type: "composite-analyze-risk-graph-timeseries-download",
  })
  const chartColors = useThemeChartColors()
  const itemsWithTheme = useMemo(
    () => applyRiskChartColorsToItems(items, chartColors),
    [items, chartColors],
  )

  let content

  const showDoubleAxisGraph = factorCategoryId === "total" && showSegment
  const showBreakdownGraph = factorCategoryId === "total" && !showSegment
  const showMultilineGraph = factorCategoryId !== "total"

  const withShowSegment = (fn) => (...args) => {
    fn(...concat(args, showSegment, [itemsWithTheme]))
  }

  const graphProps = {
    tickFormat: oneDecimalPercentageFormatter,
    startDate,
    date: selectedDate,
    setDate,
    endDate,
    data,
    onlyItem: only,
    totalLabel: "Total Risk",
    breakdownLabel: "Risk Decomposition",
    items: itemsWithTheme,
    toggle: withShowSegment(toggle),
    focusChartLine,
    blurChartLine,
    focusedChartLine,
    toggleOnly: withShowSegment(toggleOnly),
  }

  if (!earliestDate || !latestDate) {
    content = <NoData title="Predictive Risk Trends" className="graph" />
  } else {
    content = <CardContent className={classnames("card-content")}>
      <Paper className="content">
        <div className="graph-container">
          <div className="graph-top">
            {analyzeViewSelectList}
            <GraphTopBar {...{
              value: factorCategoryId,
              displayAddView,
              openDialog,
              addView: ({ id }) => addSelectedView(id),
              factors: availableSelectViewFactors,
              onTabChanged: (_, factorId) => selectFactorCategory(factorId),
              tabs: map(options, ({ id, label, value, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: (factorId) => removeSelectedView(factorId, options),
                label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
              })),
              actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, itemsWithTheme, { percents: true })} fileName={fileName} track={trackExcelDownload} />],
            }}
            />
          </div>
          <div className="graph">
            {showDoubleAxisGraph
              && <MultiLineDoubleYAxis
                tooltip={<BreakdownTooltip data={data} items={itemsWithTheme} formatter={smallPercentageFormatter} />}
                leftAxisTitle="Total Risk %"
                rightAxisTitle="Segment Risk %"
                yScaleIds={yScaleIds}
                {...graphProps}
              />}
            {showBreakdownGraph
              && <MultiLineBreakdown
                tooltip={<BreakdownTooltip data={data} items={itemsWithTheme} formatter={smallPercentageFormatter} />}
                {...graphProps}
              />}
            {showMultilineGraph
              && <MultiLine
                tooltip={<Tooltip data={data} items={itemsWithTheme} formatter={smallPercentageFormatter} />}
                {...omit(graphProps, ["totalLabel", "breakdownLabel"])}
              />}
          </div>
        </div>
        <div className="contributors">
          <RiskDetails {...{
            account,
            modelId,
            positionSetId,
            positionSetType,
            positionSetName,
            base,
            baseSignature,
            showNotHeld,
            classifications,
            allCategoryItems,
            currentGrouping,
            setGrouping,
            segment,
            showSegment,
            expand,
            startDate,
            selectedDate,
            fetchStartDate,
            viewType,
            displayType: factorCategoryId,
            drift,
            selectedOption,
            selectedViews,
            filtersVisible,
            risk,
          }}
          />
        </div>
      </Paper>
    </CardContent>
  }

  return <div className="modules_analyze-composite-risk">
    {content}
  </div>
}

AnalyzeCompositeRisk.propTypes = {
  analyzeViewSelectList: PropTypes.element,
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  positionSetId: PropTypes.string.isRequired,
  positionSetType: PropTypes.string.isRequired,
  positionSetName: PropTypes.string.isRequired,
  base: PropTypes.object,
  baseSignature: PropTypes.string,
  classifications: PropTypes.array,
  currentGrouping: PropTypes.object,
  setGrouping: PropTypes.func.isRequired,
  categories: PropTypes.array,
  allCategoryItems: PropTypes.array.isRequired,
  earliestDate: DATE,
  latestDate: DATE,
  startDate: DATE,
  endDate: DATE,
  selectedDate: DATE,
  fetchStartDate: DATE,
  period: PERIOD.isRequired,
  data: PropTypes.array.isRequired,
  risk: PropTypes.array.isRequired,
  segment: PropTypes.object,
  expand: PropTypes.bool.isRequired,
  mustAggregateFactors: PropTypes.bool.isRequired,
  favoriteFactors: PropTypes.object,
  factorsMetadata: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  factorCategoryId: PropTypes.string.isRequired,
  selectFactorCategory: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  setDate: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  drift: PropTypes.object.isRequired,
  focusedChartLine: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    delta: PropTypes.number.isRequired,
  })).isRequired,
  fileName: PropTypes.string.isRequired,
  firstDate: PropTypes.string.isRequired,
  only: PropTypes.string,
  disabled: PropTypes.array,
  selectedOption: PropTypes.object.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  lastRiskDate: DATE,
  filtersVisible: PropTypes.bool.isRequired,
  datesAdjusted: PropTypes.bool.isRequired,
  selectedViews: PropTypes.array,
  addSelectedView: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  displayAddView: PropTypes.bool.isRequired,
  availableSelectViewFactors: PropTypes.array.isRequired,
  showNotHeld: PropTypes.bool.isRequired,
  removeSelectedView: PropTypes.func.isRequired,
  yScaleIds: PropTypes.shape({
    left: PropTypes.array.isRequired,
    right: PropTypes.array,
  }),
  showSegment: PropTypes.bool.isRequired,
}
